import React from 'react';
import { Link } from 'react-router-dom';
import { CreditCounter } from './CreditComponents';
import logo from '../assets/logo.png';

const Terms = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center gap-3">
              <img src={logo} alt="AlignifyNOW" className="h-[80px] w-auto" />
              <span className="text-xl font-bold tracking-tight text-slate-900 hidden sm:inline">
                Alignify<span className="text-blue-600">NOW</span>
              </span>
            </Link>
            <div className="flex items-center space-x-4">
              <Link to="/match" className="text-sm text-gray-600 hover:text-gray-900 hidden sm:block">
                Match Score
              </Link>
              <Link to="/tracker" className="text-sm text-gray-600 hover:text-gray-900 hidden sm:block">
                Job Tracker
              </Link>
              <Link to="/pricing" className="text-sm text-gray-600 hover:text-gray-900 hidden sm:block">
                Pricing
              </Link>
              <CreditCounter className="hidden sm:flex" />
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12 sm:py-16">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 sm:p-12">
          {/* Header */}
          <div className="mb-10">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Terms of Service
            </h1>
            <p className="text-gray-500">
              Last Updated: January 21st, 2026
            </p>
            <p className="text-gray-600 mt-4 leading-relaxed">
              These Terms of Service ("Terms") govern your access to and use of the AlignifyNOW website, application, and related services ("Service"). By accessing or using the Service, you agree to be bound by these Terms. If you do not agree, you may not use the Service.
            </p>
            <p className="text-gray-600 mt-2 leading-relaxed">
              AlignifyNOW is operated as a sole proprietorship under the registered DBA "AlignifyNOW" in the State of Illinois.
            </p>
          </div>

          {/* Section 1: Payment Authorization */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-gray-900 mb-4">1. Payment Authorization</h2>
            <p className="text-gray-600 leading-relaxed">
              By providing a credit card or other payment method, you represent and warrant that you are authorized to use the designated payment method and authorize us (or our third‑party payment processor) to charge it for the total amount of your purchase, including applicable taxes and fees. You agree to keep your payment information current and accurate.
            </p>
          </section>

          {/* Section 2: Product-Specific Terms */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-gray-900 mb-4">2. Product-Specific Terms</h2>
            
            <div className="space-y-6">
              <div className="pl-4 border-l-2 border-blue-200">
                <h3 className="font-semibold text-gray-800 mb-2">2.1 Credit Packs (One‑Time Purchases)</h3>
                <p className="text-gray-600 leading-relaxed">
                  Purchases of Credits (e.g., Basic Pack, Pro Pack) provide a fixed amount of usage. These are one‑time, non‑refundable purchases. Credits do not expire, have no cash value, and may not be transferred, resold, or exchanged.
                </p>
              </div>

              <div className="pl-4 border-l-2 border-purple-200">
                <h3 className="font-semibold text-gray-800 mb-2">2.2 Monthly Subscriptions</h3>
                <p className="text-gray-600 leading-relaxed">
                  The Pro Monthly plan is a recurring subscription. Your payment method will be charged automatically at the start of each billing period. You may cancel at any time through your account settings. Cancellation takes effect at the end of the current billing cycle. We do not issue refunds for partial months or unused time.
                </p>
              </div>

              <div className="pl-4 border-l-2 border-green-200">
                <h3 className="font-semibold text-gray-800 mb-2">2.3 Lifetime Access</h3>
                <p className="text-gray-600 leading-relaxed">
                  The Lifetime Access plan is a one‑time purchase that includes a <strong>7‑Day Money‑Back Guarantee</strong>. Refund requests must be submitted within 7 calendar days of purchase. After this period, the purchase is final and non‑refundable. "Lifetime Access" refers to the lifetime of the AlignifyNOW product, not the lifetime of the user. If the Service is discontinued, materially changed, or replaced, we may provide an equivalent alternative at our discretion.
                </p>
              </div>
            </div>
          </section>

          {/* Section 3: Changes to Pricing */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-gray-900 mb-4">3. Changes to Pricing</h2>
            <p className="text-gray-600 leading-relaxed">
              We may modify pricing at any time. Any changes to subscription pricing will take effect at the start of your next billing cycle following email notice. Price changes do not apply retroactively.
            </p>
          </section>

          {/* Section 4: Refund & Cancellation Policy */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-gray-900 mb-4">4. Refund & Cancellation Policy</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">4.1 Subscription Services</h3>
                <ul className="text-gray-600 leading-relaxed list-disc pl-5 space-y-1">
                  <li>You may cancel at any time.</li>
                  <li>Access continues through the end of the current billing cycle.</li>
                  <li>No refunds for partial months or unused time.</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2">4.2 Lifetime Access</h3>
                <ul className="text-gray-600 leading-relaxed list-disc pl-5 space-y-1">
                  <li>Full refund available within 7 days of purchase.</li>
                  <li>After 7 days, the purchase is final.</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2">4.3 Credit Packs</h3>
                <ul className="text-gray-600 leading-relaxed list-disc pl-5 space-y-1">
                  <li>All credit pack purchases are final and non‑refundable.</li>
                  <li>Credits are consumed immediately upon use due to AI processing costs.</li>
                </ul>
              </div>

              <p className="text-gray-600 leading-relaxed">
                We may, at our sole discretion, issue refunds for fraudulent or unauthorized transactions.
              </p>
            </div>
          </section>

          {/* Section 5: Nature of Service & No Guarantee */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-gray-900 mb-4">5. Nature of Service & No Guarantee of Results</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">5.1 No Employment Guarantee</h3>
                <p className="text-gray-600 leading-relaxed">
                  AlignifyNOW provides tools for resume analysis, job tracking, and interview preparation. We do not guarantee job interviews, job offers, or employment outcomes. Your results depend on factors outside our control.
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h3 className="font-semibold text-yellow-800 mb-2">5.2 AI Disclaimer</h3>
                <p className="text-yellow-700 leading-relaxed">
                  The Service uses Artificial Intelligence to generate feedback and content. AI outputs may be inaccurate, incomplete, biased, or inconsistent. You are solely responsible for reviewing and verifying all AI‑generated content before using it. We are not liable for any decisions, outcomes, or consequences arising from reliance on AI‑generated content.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-800 mb-2">5.3 Zero‑Retention Guarantee</h3>
                <p className="text-blue-700 leading-relaxed">
                  We do not store, retain, or back up any resumes, job descriptions, or documents you upload. All processing occurs in temporary system memory and is automatically discarded. We cannot recover lost files. You are responsible for saving your own documents.
                </p>
              </div>
            </div>
          </section>

          {/* Section 6: Third-Party Services */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-gray-900 mb-4">6. Use of Platform Providers & Third‑Party Services</h2>
            <p className="text-gray-600 leading-relaxed">
              The Service is built on infrastructure provided by Emergent, which integrates third‑party tools such as OpenAI, Stripe, MongoDB, and Google. We do not control these providers and are not responsible for their performance, availability, or data handling practices. Your use of the Service constitutes your agreement to the terms and policies of these third‑party providers.
            </p>
          </section>

          {/* Section 7: Account Security */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-gray-900 mb-4">7. Account Security Authentication & Account Access</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              AlignifyNOW uses Google Sign In as its sole authentication method. By using the Service, you agree that access to your account is tied to your Google account credentials. We do not offer alternative login methods, manual account creation, or email/password authentication. You are solely responsible for maintaining access to your Google account. If you lose access to your Google account, we are unable to restore, transfer, or modify your AlignifyNOW account, including Lifetime Access or purchased credits. We may, at our discretion, request proof of purchase for verification purposes, but we do not guarantee account recovery in such cases.
            </p>
            <p className="text-gray-600 leading-relaxed mb-2">You are responsible for:</p>
            <ul className="text-gray-600 leading-relaxed list-disc pl-5 space-y-1 mb-4">
              <li>maintaining the confidentiality of your login credentials</li>
              <li>all activity occurring under your account</li>
              <li>notifying us immediately of any unauthorized access or security breach</li>
            </ul>
            <p className="text-gray-600 leading-relaxed">
              We are not liable for losses arising from compromised accounts, shared accounts, or misuse of your credentials.
            </p>
          </section>

          {/* Section 8: Prohibited Conduct */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-gray-900 mb-4">8. Prohibited Conduct</h2>
            <p className="text-gray-600 leading-relaxed mb-2">You agree not to:</p>
            <ul className="text-gray-600 leading-relaxed list-disc pl-5 space-y-1 mb-4">
              <li>use the Service for commercial purposes such as client‑based resume writing or bulk processing</li>
              <li>share, sell, or transfer your account</li>
              <li>scrape, harvest, or extract data from the Service</li>
              <li>use automation, bots, or scripts to access or interact with the Service</li>
              <li>reverse engineer, decompile, or attempt to access the underlying code</li>
              <li>interfere with or disrupt the Service</li>
              <li>use the Service for unlawful, harmful, or misleading purposes</li>
            </ul>
            <p className="text-gray-600 leading-relaxed font-medium">
              Violation of this section may result in immediate suspension or termination.
            </p>
          </section>

          {/* Section 9: Fair Use Policy */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-gray-900 mb-4">9. Fair Use Policy</h2>
            <p className="text-gray-600 leading-relaxed">
              "Unlimited AI Scans" are subject to a reasonable use limit of 50 scans per day per account. We may throttle, suspend, or investigate accounts suspected of abuse, including automation or shared use.
            </p>
          </section>

          {/* Section 10: Termination */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-gray-900 mb-4">10. Termination</h2>
            <p className="text-gray-600 leading-relaxed">
              We may suspend or terminate your access to the Service at any time, with or without notice, if we believe you have violated these Terms, engaged in fraudulent activity, or posed a risk to the integrity of the Service. Upon termination, you remain responsible for all charges incurred prior to termination.
            </p>
          </section>

          {/* Section 11: Limitation of Liability */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-gray-900 mb-4">11. Limitation of Liability</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">11.1 "As Is" Service</h3>
                <p className="text-gray-600 leading-relaxed">
                  The Service is provided "AS IS" and "AS AVAILABLE," without warranties of any kind.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2">11.2 Limitation of Damages</h3>
                <p className="text-gray-600 leading-relaxed">
                  To the maximum extent permitted by law, AlignifyNOW and its owners, affiliates, and licensors are not liable for indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or goodwill.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2">11.3 Monetary Cap</h3>
                <p className="text-gray-600 leading-relaxed">
                  Our total liability for all claims shall not exceed the amount you paid to AlignifyNOW in the twelve (12) months preceding the claim.
                </p>
              </div>
            </div>
          </section>

          {/* Section 12: Governing Law */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-gray-900 mb-4">12. Governing Law</h2>
            <p className="text-gray-600 leading-relaxed">
              These Terms are governed by and construed in accordance with the laws of the State of Illinois, without regard to conflict‑of‑law principles. Any disputes shall be resolved exclusively in the state or federal courts located in Illinois.
            </p>
          </section>

          {/* Section 13: International Use */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-gray-900 mb-4">13. International Use</h2>
            <p className="text-gray-600 leading-relaxed">
              We make no representation that the Service is appropriate or available outside the United States. If you access the Service from another jurisdiction, you do so at your own risk and are responsible for compliance with local laws.
            </p>
          </section>

          {/* Section 14: No Transfer of Rights */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-gray-900 mb-4">14. No Transfer of Rights</h2>
            <p className="text-gray-600 leading-relaxed">
              Your rights and obligations under these Terms are personal to you and may not be assigned, transferred, sublicensed, or otherwise conveyed to any other person or entity without our prior written consent. Any attempted assignment or transfer in violation of this section is void.
            </p>
          </section>

          {/* Section 15: Severability */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-gray-900 mb-4">15. Severability</h2>
            <p className="text-gray-600 leading-relaxed">
              If any provision of these Terms is found to be invalid, unlawful, or unenforceable, that provision shall be enforced to the maximum extent permitted by law, and the remaining provisions shall continue in full force and effect.
            </p>
          </section>

          {/* Section 16: Entire Agreement */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-gray-900 mb-4">16. Entire Agreement</h2>
            <p className="text-gray-600 leading-relaxed">
              These Terms constitute the entire agreement between you and AlignifyNOW regarding your use of the Service and supersede all prior or contemporaneous understandings, agreements, representations, or communications, whether written or oral.
            </p>
          </section>

          {/* Contact Section */}
          <section className="bg-gray-50 rounded-xl p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-3">
              17. Contact Us
            </h2>
            <p className="text-gray-600 mb-4">
              For questions or support, contact:
            </p>
            <a 
              href="mailto:support@alignifynow.com" 
              className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              support@alignifynow.com
            </a>
          </section>
        </div>

        {/* Back to Home */}
        <div className="mt-8 text-center">
          <Link to="/" className="text-gray-500 hover:text-gray-700 text-sm">
            ← Back to Home
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center mb-4 md:mb-0">
              <span className="text-xl font-bold text-white opacity-80">
                Alignify<span className="text-blue-400">NOW</span>
              </span>
            </div>
            <div className="text-gray-400 text-xs mb-4 md:mb-0">
              © 2026 AlignifyNOW. All rights reserved. Built by Matambanadzo Studio.
            </div>
            <div className="flex items-center space-x-6">
              <Link to="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-white text-sm transition-colors">
                Terms of Service
              </Link>
              <Link to="/affiliates" className="text-gray-400 hover:text-white text-sm transition-colors">
                Affiliates
              </Link>
              <Link to="/checklist" className="text-gray-400 hover:text-white text-sm transition-colors">
                Free Checklist
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Terms;
